import React from 'react';
import Icon from '../../../components/AppIcon';

const relTime = (ts) => {
  if (!ts) return '';
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const PLATFORM = {
  wordpress: { color: '#21759B', bg: '#E0F2FE' },
  pinterest: { color: '#E60023', bg: '#FEE2E2' },
  facebook: { color: '#1877F2', bg: '#EFF6FF' },
  etsy: { color: '#F56400', bg: '#FEF3C7' }
};

const STATUS_ICON = {
  success: { name: 'CheckCircle', color: '#22C55E' },
  error: { name: 'XCircle', color: '#EF4444' },
  processing: { name: 'Loader', color: '#3B82F6', spin: true },
  pending: { name: 'Clock', color: '#F59E0B' }
};

const RecentActivityFeed = ({ activities = [], loading }) => {
  const shown = activities.slice(0, 20); // render up to 20, card height limits visible

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm flex flex-col">
        <div className="px-5 py-4 border-b border-[#F1F5F9]">
          <div className="h-4 bg-slate-100 rounded w-1/3 mb-1" />
          <div className="h-3 bg-slate-100 rounded w-1/2" />
        </div>
        <div className="p-4 space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-7 h-7 rounded-lg bg-slate-100 flex-shrink-0" />
              <div className="flex-1 h-3 bg-slate-100 rounded" />
              <div className="w-10 h-3 bg-slate-100 rounded flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm flex flex-col">
      <div className="px-5 py-4 border-b border-[#F1F5F9] flex items-center justify-between flex-shrink-0">
        <div>
          <h3 className="text-sm font-semibold text-[#0F172A]">Recent Activity</h3>
          <p className="text-[11px] text-[#94A3B8] mt-0.5">Latest automation events</p>
        </div>
        {shown.length > 0 && (
          <span className="text-[10px] text-[#94A3B8] bg-[#F8FAFC] border border-[#E5E7EB] px-2 py-0.5 rounded-full">
            {shown.length} events
          </span>
        )}
      </div>

      {/* Fixed height scrollable area */}
      <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
        {shown.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-[#94A3B8] gap-2">
            <Icon name="Activity" size={22} className="opacity-30" />
            <p className="text-sm">No recent activity</p>
          </div>
        ) : (
          <div className="p-3 space-y-0.5">
            {shown.map(item => {
              const p = PLATFORM[item.platform] || PLATFORM.wordpress;
              const s = STATUS_ICON[item.status] || STATUS_ICON.pending;
              return (
                <div
                  key={item.id}
                  className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-[#F8FAFC] transition-colors"
                >
                  {/* Platform icon */}
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: p.bg }}
                  >
                    <Icon name={item.icon} size={13} style={{ color: p.color }} />
                  </div>

                  {/* Status icon */}
                  <Icon
                    name={s.name}
                    size={12}
                    className={`flex-shrink-0 ${s.spin ? 'animate-spin' : ''}`}
                    style={{ color: s.color }}
                  />

                  {/* Title */}
                  <span className="flex-1 text-xs text-[#0F172A] truncate min-w-0">{item.event}</span>

                  {/* Time */}
                  <span className="text-[10px] text-[#94A3B8] flex-shrink-0 tabular-nums">
                    {relTime(item.timestamp)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentActivityFeed;
